import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { fetchFeatureInterest } from 'pricing-pages-lib/api/featureInterestApi';
import { fetchLimitUsage } from 'pricing-pages-lib/api/fetchLimitUsage';
import { fetchUpgradeConfig } from 'pricing-pages-lib/api/fetchUpgradeConfig';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { getQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
import { filterLimits, sortLimits } from 'pricing-pages-lib/utils/usageLimitUtils';
import { SCREENS } from 'pricing-pages-ui/constants/tracking/Locations';
import { useEffect, useState } from 'react';
import { NavMarker } from 'react-rhumb';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { buildFilteredHighlightedProducts } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { getHubAndTierFromApiName } from 'ui-addon-upgrades/ums/utils';
import { getMerchCardsToShow } from '../../constants/merchCards/MerchCardsToShow';
import Screens from '../../constants/routing/Screens';
import { useAnchorLinkScroll } from '../../hooks/htmlElementHooks';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useFeatureStore } from '../../hooks/useFeatureStore';
import { useTrackRetail } from '../../hooks/useTrackRetail';
import ProductInfoContainer from '../common/ProductInfoContainer';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import SuiteFeatureTable from '../feature-table/suite-feature-table/SuiteFeatureTable';
import WhyGoHubSpotSection from '../free-tools-page/WhyGoHubSpotSection';
import { SuiteTiles } from '../merch-card/SuiteTiles';
import ComparePlansLink from './ComparePlansLink';
import SuiteStarterHeroBanner from './SuiteStarterHeroBanner';
import UpgradeModalHeroBanner from './UpgradeModalHeroBanner';
import FeatureInterestAndLimitUsageSection from './feature-interest/FeatureInterestAndLimitUsageSection';

const SuiteStarterPage = () => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle('starterCrmSuite');
  const [upgradeConfig, setUpgradeConfig] = useState();
  const [limitUsages, setLimitUsages] = useState();
  const [featureInterest, setFeatureInterest] = useState();
  const [showUpgradeModalContent, setShowUpgradeModalContent] = useState(false);
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen: SCREENS.SUITE_STARTER
    });
  }, []);
  useEffect(() => {
    const {
      upgradeSource
    } = getQueryParams();
    fetchUpgradeConfig(upgradeSource).then(config => {
      const {
        tier
      } = getHubAndTierFromApiName(config.apiNames[0]);

      if (tier === STARTER.toLocaleLowerCase() && config.pointType === 'LOCKED_FEATURE') {
        setUpgradeConfig(config);
        setShowUpgradeModalContent(true);
      } else {
        setUpgradeConfig(null);
      }
    }).catch(() => {
      setUpgradeConfig(null);
    });
  }, []);
  useEffect(() => {
    fetchLimitUsage().then(limitUsageResponse => {
      setLimitUsages(sortLimits(filterLimits(limitUsageResponse)));
    }).catch(() => {
      setLimitUsages([]);
    });
    fetchFeatureInterest(STARTER).then(featureInterestResponse => {
      setFeatureInterest(featureInterestResponse);
    }).catch(() => {
      setFeatureInterest([]);
    });
  }, []);
  const productLevels = getMerchCardsToShow({
    isFreeToStarter: true
  });
  const marketingHighlightedProducts = buildFilteredHighlightedProducts({
    productLine: MARKETING,
    productLevels
  });
  useFeatureStore({
    highlightedProducts: marketingHighlightedProducts,
    productLine: MARKETING
  });
  const salesHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: SALES
  });
  useFeatureStore({
    highlightedProducts: salesHighlightedProducts,
    productLine: SALES
  });
  const serviceHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: SERVICE
  });
  useFeatureStore({
    highlightedProducts: serviceHighlightedProducts,
    productLine: SERVICE
  });
  const operationsHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: OPERATIONS
  });
  useFeatureStore({
    highlightedProducts: operationsHighlightedProducts,
    productLine: OPERATIONS
  });
  const cmsHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: CMS
  });
  useFeatureStore({
    highlightedProducts: cmsHighlightedProducts,
    productLine: CMS
  });
  const productsByProductLine = {
    [MARKETING]: marketingHighlightedProducts,
    [SALES]: salesHighlightedProducts,
    [SERVICE]: serviceHighlightedProducts,
    [OPERATIONS]: operationsHighlightedProducts,
    [CMS]: cmsHighlightedProducts
  };
  const isLoading = upgradeConfig === undefined;
  const isLoadingFeatureInterest = !featureInterest;
  const isLoadingLimitUsages = !limitUsages;
  return /*#__PURE__*/_jsxs("div", {
    "data-selenium-id": "page-success",
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "SUITE_STARTER_PAGE"
    }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: isLoading,
      heights: [705],
      widths: ['100%'],
      Content: showUpgradeModalContent ? UpgradeModalHeroBanner : SuiteStarterHeroBanner,
      contentProps: showUpgradeModalContent ? {
        upgradeConfig
      } : {}
    }), /*#__PURE__*/_jsxs(ProductInfoContainer, {
      style: {
        margin: 'auto'
      },
      children: [/*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isLoading,
        Content: !showUpgradeModalContent ? SuiteTiles : undefined,
        contentProps: !showUpgradeModalContent ? {
          isFreeToStarter: true
        } : undefined
      }), isLoadingFeatureInterest || isLoadingLimitUsages ? /*#__PURE__*/_jsx(UILoadingSpinner, {
        minHeight: 200
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ComparePlansLink, {}), /*#__PURE__*/_jsx(FeatureInterestAndLimitUsageSection, {
          limitUsages: limitUsages,
          featureInterest: featureInterest
        }), /*#__PURE__*/_jsx(SuiteFeatureTable, {
          productsByProductLine: productsByProductLine,
          screen: Screens.SUITE_STARTER,
          isFreeToStarter: true
        })]
      }), /*#__PURE__*/_jsx(WhyGoHubSpotSection, {
        selectedNavItem: "suite-starter"
      })]
    })]
  });
};

export default SuiteStarterPage;